/**
 * Home body (specs/home-page AC-5 revised, §7): Today's challenge → Daily streak + Weekly row → the Active
 * row (when there is one) → the Invite row (when there is one) → Last results (when there is neither), one
 * vertical page scroll. Presentational: every region's data and handlers arrive as props so the screen
 * renders in tests and Storybook without providers. The slate, streak and weekly regions are slots.
 */

import React, { useMemo, type ReactNode } from 'react';
import { View, ScrollView } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import type { HomeActiveTileModel, HomeInviteTileModel, HomeResultTileModel } from '../../../src/home/types/home.types';
import { ActiveChallengeTile } from '../ActiveChallengeTile/ActiveChallengeTile';
import { InviteTile } from '../InviteTile/InviteTile';
import { LastResultsCard } from '../LastResultsCard/LastResultsCard';
import { HomeRegionError } from '../HomeRegionState/HomeRegionError';
import { getHomeScreenStyles } from './HomeScreen.styles';

export interface HomeTilesRegionProps {
  active: HomeActiveTileModel | null;
  invite: HomeInviteTileModel | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onPressActive: (challengeId: string) => void;
  onPressInvite: (challengeId: string) => void;
  onAcceptInvite: (challengeId: string, stakeCoins: number) => void;
  acceptingId: string | null;
}

export interface HomeResultsRegionProps {
  results: HomeResultTileModel[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onPressResult: (challengeId: string) => void;
  onChallengeFriend: () => void;
  onFindRival: () => void;
}

export interface HomeScreenViewProps {
  /** Today's challenge card. */
  slateSlot?: ReactNode;
  /** Daily streak card (left of Weekly). */
  streakSlot?: ReactNode;
  /** Weekly quest card. */
  weeklySlot?: ReactNode;
  tiles: HomeTilesRegionProps;
  results: HomeResultsRegionProps;
  nowMs?: number;
}

/** Last results only takes the floor when the challenge list has loaded and holds nothing live or pending (AC-29). */
export function showsLastResults(tiles: Pick<HomeTilesRegionProps, 'active' | 'invite' | 'isLoading' | 'isError'>): boolean {
  return !tiles.isLoading && !tiles.isError && tiles.active == null && tiles.invite == null;
}

export const HomeScreenView: React.FC<HomeScreenViewProps> = ({ slateSlot, streakSlot, weeklySlot, tiles, results, nowMs }) => {
  const theme = useTheme();
  const styles = useMemo(() => getHomeScreenStyles(theme), [theme]);
  const hasMiddleRow = streakSlot != null || weeklySlot != null;

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
      testID={HOME_TEST_IDS.scroll}
    >
      <View style={styles.column}>
        {slateSlot != null && <View testID={HOME_TEST_IDS.slateRegion}>{slateSlot}</View>}

        {hasMiddleRow && (
          <View style={styles.row}>
            {streakSlot != null && (
              <View style={styles.rowItem} testID={HOME_TEST_IDS.streakRegion}>
                {streakSlot}
              </View>
            )}
            {weeklySlot != null && (
              <View style={styles.rowItem} testID={HOME_TEST_IDS.weeklyRegion}>
                {weeklySlot}
              </View>
            )}
          </View>
        )}

        {tiles.isError ? (
          <HomeRegionError onRetry={tiles.onRetry} testID={`${HOME_TEST_IDS.regionError}-tiles`} />
        ) : (
          <>
            <ActiveChallengeTile model={tiles.active} isLoading={tiles.isLoading} onPress={tiles.onPressActive} nowMs={nowMs} />
            <InviteTile
              model={tiles.invite}
              isLoading={tiles.isLoading}
              isAccepting={tiles.acceptingId != null && tiles.acceptingId === tiles.invite?.challengeId}
              onAccept={tiles.onAcceptInvite}
              onPress={tiles.onPressInvite}
            />
            {showsLastResults(tiles) && (
              <LastResultsCard
                results={results.results}
                isLoading={results.isLoading}
                isError={results.isError}
                onRetry={results.onRetry}
                onPressResult={results.onPressResult}
                onChallengeFriend={results.onChallengeFriend}
                onFindRival={results.onFindRival}
              />
            )}
          </>
        )}
      </View>
    </ScrollView>
  );
};
